/**
 * Well Servicing batch 6 — the AI drafts (fieldiq-api#1296, WS-6). Typed from
 * routes/well-servicing-ai.routes.ts and lib/ws-ai-digests.ts as merged at
 * c65c6424; the manifest mirror carries the same routes.
 *
 * camelCase reads, `{ success, data }`. Every run is a HITL draft (agent_runs,
 * outcome 'pending'): `data` is the API's deterministic result, `draft` the
 * model's text (null when it was skipped or rejected — `aiError` says why).
 * Nothing is published until a person approves it.
 */

export const WS_AI_AGENTS = ['ws_tour_summary', 'ws_ticket_preflight', 'ws_well_report_draft', 'ws_compliance_digest'] as const;
export type WsAiAgent = (typeof WS_AI_AGENTS)[number];

export interface WsAiDraft {
  narrative: string;
  observations: string[];
}

// ─── Tour summary ────────────────────────────────────────────────────────────

export interface WsTourSummaryData {
  tour: {
    id: string;
    workDate: string;
    status: string;
    startTime: string | null;
    endTime: string | null;
    jobId: string;
    jobNumber: string;
    rigNumber: string | null;
    wellName: string | null;
    customerName: string | null;
  };
  depthUnit: string;
  hoursByBillingClass: Record<string, number>;
  timeline: { tourHours: number | null; coveredHours: number | null; gaps: Array<{ from: string; to: string; minutes: number }>; complete: boolean | null };
  activities: number | null;
  codes: Array<{ code: string; label: string | null; billingClass: string; hours: number }>;
  crew: Array<{ name: string; position: string | null; shift: string | null; hours: number | null }>;
  crewHours: number | null;
  depth: { first: number | null; last: number | null; deepest: number | null; summary: string | null };
  stringEvents: Array<{ action: string; kind: string; quantity: number; length: string | null }>;
  tallies: Array<{ kind: string; direction: string; joints: number; totalLength: number | null; lengthUnit: string }>;
  pressureTests: Array<{ testedOn: string | null; kind: string; result: string }>;
  drills: Array<{ drilledOn: string | null; kind: string; secondsToSecure: number | null }>;
  fuelUsedLitres: number | null;
  remarks: string | null;
}

// ─── Preflight (one ticket, or one job's open tickets and approved sheets) ──

export interface WsPreflightRateGap {
  key: string;
  label: string;
  days: string[];
  reason: string | null;
  codes?: string[];
}

export interface WsPreflightData {
  scope: 'ticket' | 'job';
  job: { id: string; jobNumber: string };
  ticket: { id: string; ticketNumber: string; status: string; scope: string; workDate: string | null } | null;
  currency: string | null;
  ticketsChecked: number | null;
  sheetsChecked: number | null;
  unpricedLines: Array<{ ticketId: string; ticketNumber: string; lineId: string; description: string | null; reason: string | null }>;
  sheetsWithoutTicket: Array<{ tourId: string; workDate: string }>;
  classesWithoutRate: WsPreflightRateGap[];
  subsistenceWithoutRate: WsPreflightRateGap[];
  rentInsWithoutRate: WsPreflightRateGap[];
  flagCount: number | null;
  ready: boolean | null;
}

// ─── Well report draft ───────────────────────────────────────────────────────

export interface WsReportDraftData {
  report: { id: string; status: string };
  job: { id: string; jobNumber: string };
  content: unknown;
}

// ─── Compliance digest ───────────────────────────────────────────────────────

export interface WsComplianceItemBase {
  state: string;
  dueOn: string | null;
}

export interface WsComplianceDigestData {
  today: string;
  crewCerts: Array<WsComplianceItemBase & { userId: string; name: string; cert: string; jobNumbers: string[] }>;
  pressureControl: Array<WsComplianceItemBase & { equipmentId: string; label: string; component: string | null; jobNumbers: string[] }>;
  stackGuard: Array<WsComplianceItemBase & { equipmentId: string; label: string; jobNumber: string | null; plannedEndOn: string | null; code: string | null; error: string | null }>;
  gauges: Array<WsComplianceItemBase & { equipmentId: string; label: string }>;
  total: number | null;
}

// ─── A run, as POST returns it and as the register stores it ─────────────────

/** POST /ai/* — 201. */
export interface WsAiRunResult<T = unknown> {
  agentRunId: string;
  agent: WsAiAgent | string;
  status: 'draft' | string;
  data: T | null;
  draft: WsAiDraft | null;
  aiError: string | null;
}

/** GET /ai/digests (≤ 20, newest first) and GET /ai/digests/:id — shapeWsRun. */
export interface WsAiStoredRun<T = unknown> {
  agentRunId: string;
  agent: WsAiAgent | string;
  status: string;
  /** agent_runs.outcome: pending | actioned | dismissed | superseded | no_action. */
  outcome: string | null;
  trigger: string | null;
  triggerData: Record<string, unknown> | null;
  data: T | null;
  draft: WsAiDraft | null;
  approved: WsAiDraft | null;
  approvedEdited: boolean | null;
  aiError: string | null;
  createdAt: string | null;
  reviewedAt: string | null;
}

/** POST /ai/digests/:id/approve — omit both to approve the draft as written. */
export interface WsAiApproveBody {
  narrative?: string;
  observations?: string[];
}
